import { Box, Grid, Typography } from '@mui/material'
import { LoanLifeCycleState } from '@shared/Enums'
import { LoanCTO, LoanCTOSchema } from '@shared/LoanCTO'
import { EthDate } from '@shared/conversions'
import dayjs from 'dayjs'
import React, { FC, useEffect, useState } from 'react'

import { TimeLine } from '@react/components/common/timeline/Timeline'
import ViewField from '@react/components/elements/ViewField'

import { PoolEventCTO } from '@shared/sharedTypes'

import { EventTypeForTemplate } from '@shared/templateApx'

import { fetchPoolEventCTOs } from '@react/fetchers/PoolFetchers'
import { useOtChainState, useOtChainTimestamp, useOtSelector } from '@react/redux/store'
import { formatDate } from '@react/utils/dates'
import { logOT } from '@shared/loggingShared'

interface LoanDetailsProps {
  loan: LoanCTO
}

const hasPassed = (d: EthDate, chainTimestamp: number): boolean =>
  dayjs(Number(d) * 1000).diff(dayjs(Number(chainTimestamp) * 1000), 'days') <= 0

// TODO: find by eventType may need conversion from eventEnum -> string first
const hasEvent = (events: any[], eventType: EventTypeForTemplate): boolean =>
  events.find((e) => e.eventType === eventType) != null

const getEvent = (events: any[], eventType: EventTypeForTemplate): PoolEventCTO =>
  events.find((e) => e.eventType === eventType)

export const LoanDetails: FC<LoanDetailsProps> = ({ loan }): React.ReactElement => {
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()

  const poolOverviewCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[loan.poolAddr])

  const chainTimestamp = useOtChainTimestamp()

  const [loanEvents, setLoanEvents] = useState<PoolEventCTO[]>([])

  useEffect(() => {
    async function fetchLoanEvents(): Promise<void> {
      if (!providerReadOnly) return

      const allPoolEvents = await fetchPoolEventCTOs(serverApiBaseUrl, restAPIKey, loan.poolAddr)
      const events = allPoolEvents.filter((e) => e.loanAddr === loan.loanAddr)

      logOT.info('#LoanEvents', events, allPoolEvents)
      setLoanEvents(events)
    }
    void fetchLoanEvents()
  }, [serverApiBaseUrl, restAPIKey, loan.poolAddr, loan.loanAddr])
  const liquidityTokenSymbol = poolOverviewCTO.liquidityTokenSymbol
  const symbol = loan.symbol ?? 'PPT'

  const loanTimeLineBlocks = [
    {
      eventDate: loan.depositClosingTimestamp,
      header: 'Investment Window Closes',
      completed: hasEvent(loanEvents, EventTypeForTemplate.LoanRolloverInitiated),
      completedTimestamp: getEvent(loanEvents, EventTypeForTemplate.LoanRolloverInitiated)?.timestamp,
      caption: 'Any deposits made after this date will not be drawn until the next loan',
    },
    {
      eventDate: loan.accrualStartTimestamp,
      header: 'Loan Commencement Date',
      completed: hasEvent(loanEvents, EventTypeForTemplate.LoanRolloverFinalized),
      completedTimestamp: getEvent(loanEvents, EventTypeForTemplate.LoanRolloverFinalized)?.timestamp,
      caption: 'Confirmed rate set and loan begins accruing interest and fees',
    },
    {
      eventDate: loan.redemptionRequestClosingTimestamp,
      header: 'Deadline to submit Rollover Termination Notice',
      caption: 'Principal and interest will be compounded and rolled into a new loan',
      completed: hasPassed(loan.redemptionRequestClosingTimestamp, chainTimestamp),
    },
    {
      eventDate: loan.maturingTimestamp,
      header: 'Loan Maturity Date',
      caption: 'Interest and fee accrual ends and Transfer Out Window commences',
      completed: hasEvent(loanEvents, EventTypeForTemplate.LoanMatured),
      completedTimestamp: getEvent(loanEvents, EventTypeForTemplate.LoanMatured)?.timestamp,
    },
    {
      eventDate: loan.redemptionAvailableTimestamp,
      header: 'Payment Date',
      caption: `${liquidityTokenSymbol} will be available for withdrawal`,
      completed: hasEvent(loanEvents, EventTypeForTemplate.LoanRepayment),
      completedTimestamp: getEvent(loanEvents, EventTypeForTemplate.LoanRepayment)?.timestamp,
    },
  ]

  // Some temporary conversion from ERC20Wei back to Big number back to Number to display with decimals
  const exr = Number((BigInt(loan.exchangeRateAtMaturity) * 100n) / BigInt(1e12)) / 100

  return (
    <Box px={4}>
      <Grid alignItems={'stretch'} container spacing={4} sx={{ flexGrow: 1, marginBottom: 8 }} py={2}>
        <Grid item xs={6}>
          <ViewField dataObject={loan} fieldSchema={LoanCTOSchema.describe().fields.state}></ViewField>
        </Grid>
        <Grid item xs={6}>
          <ViewField dataObject={loan} fieldSchema={LoanCTOSchema.describe().fields.durationDays}></ViewField>
        </Grid>
        <Grid item xs={6}>
          <ViewField
            dataObject={loan}
            fieldSchema={LoanCTOSchema.describe().fields.principal}
            liquidityTokenSymbol={liquidityTokenSymbol}
          ></ViewField>
        </Grid>
        <Grid item xs={6}>
          {loan.state === LoanLifeCycleState.Active ? (
            <ViewField dataObject={loan} fieldSchema={LoanCTOSchema.describe().fields.finalizedApr}></ViewField>
          ) : (
            <ViewField dataObject={loan} fieldSchema={LoanCTOSchema.describe().fields.indicativeApr}></ViewField>
          )}
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ flexDirection: 'column', alignContent: 'left' }}>
            <Typography variant="bodyMuted" gutterBottom component="div" textAlign={'left'}>
              Exchange Rate at Maturity
            </Typography>
            <Typography variant="bodyBold" gutterBottom component="div" textAlign={'left'}>
              {(exr / 1e6).toFixed(5)} {liquidityTokenSymbol}/{symbol}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <TimeLine
        blocks={loanTimeLineBlocks}
        CollapsedContent={
          <Typography variant="bodyMuted" sx={{ margin: 0 }}>
            Accrual {formatDate(loan.accrualStartTimestamp)} - {formatDate(loan.maturingTimestamp)}. Expand for details.
          </Typography>
        }
      />
    </Box>
  )
}
